import React from 'react';

import Table, { SortableTh } from '../Table';
import useSort from '../Table/useSort';
import Pagination from '../Pagination';
import TextInput from '../TextInput';

import styles from './OTDataTable.module.css';

// A self-contained table for the OT protocol pages, whose payload is the uniform
// { Cols: string[], Rows: any[][] } shape. It does three things in memory:
//   * search  — case-insensitively matches ANY cell in a row
//   * sort    — click a header (asc -> desc -> off) via useSort, keyed by the
//               column index since the rows are arrays, not keyed objects
//   * paginate — slices the searched+sorted set
// One component so BacNET / DNP3 / Modbus / S7 Comms behave identically and there
// is a single place to change the behaviour.
const OTDataTable = ({
	title,
	cols = [],
	rows = [],
	pageLimit = 10,
	containerStyle,
	searchable = true,
	searchPlaceholder = 'Search here',
}) => {
	const [ searchKey, setSearchKey ] = React.useState('');
	const [ pageNumber, setPageNumber ] = React.useState(1);

	// Memoised so a non-array prop's `[]` fallback keeps a stable reference — else
	// the filter useMemo and the page-reset useEffect below would fire every render.
	const safeCols = React.useMemo(() => (Array.isArray(cols) ? cols : []), [ cols ]);
	const safeRows = React.useMemo(() => (Array.isArray(rows) ? rows : []), [ rows ]);

	const filtered = React.useMemo(() => {
		const q = String(searchKey ?? '').trim().toLowerCase();
		if (!q) return safeRows;
		return safeRows.filter(
			(row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').toLowerCase().includes(q))
		);
	}, [ safeRows, searchKey ]);

	const { sorted, sortKey, sortDir, requestSort } = useSort(filtered);
	const sort = { sortKey, sortDir, requestSort };

	const total = sorted.length;
	const pageRows = React.useMemo(
		() => sorted.slice((pageNumber - 1) * pageLimit, pageNumber * pageLimit),
		[ sorted, pageNumber, pageLimit ]
	);

	// Snap back to the first page whenever the result set changes underfoot (a new
	// search, a new sort, or freshly loaded rows) so the view is never past the end.
	React.useEffect(() => {
		setPageNumber(1);
	}, [ searchKey, sortKey, sortDir, safeRows ]);

	return (
		<div className={styles.container}>
			{(title || searchable) && (
				<div className={styles.heading}>
					{title ? <h3 className={styles.headingText}>{title}</h3> : <span />}
					{searchable && (
						<TextInput
							icon='search'
							placeholder={searchPlaceholder}
							value={searchKey}
							onChange={(e) => setSearchKey(e.target.value)}
							style={{ width: '15rem' }}
						/>
					)}
				</div>
			)}
			<Table
				containerStyle={containerStyle || { height: '70vh' }}
				tableHead={
					<tr>
						{safeCols.map((col, index) => (
							<SortableTh key={col + index} label={col} field={index} sort={sort} />
						))}
					</tr>
				}
				tableRow={
					total > 0 && pageRows.length > 0 ? (
						pageRows.map((row, index) => (
							<tr key={index}>
								{Array.isArray(row) && row.map((cell, ci) => <td key={ci}>{cell}</td>)}
							</tr>
						))
					) : (
						<tr>
							<td colSpan={safeCols.length || 1} style={{ textAlign: 'center' }}>
								No data found
							</td>
						</tr>
					)
				}
			/>
			<div className='paginationContainer'>
				<Pagination
					onPageChange={(cp) => setPageNumber(cp.selected + 1)}
					rowsPerPage={pageLimit}
					totalPageSize={total}
					pageNumber={pageNumber}
				/>
			</div>
		</div>
	);
};

export default OTDataTable;
